import { useState, useEffect, useCallback } from 'react';
import { Trophy, Trash2, Plus, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { deriveOutcome } from '@/lib/parse-match-score';
import {
  useSessionMatchResults,
  useUpsertMatchResult,
  useUpdateMatchScore,
  useDeleteMatchResult,
} from '@/hooks/use-match-results';

interface Player {
  id: string;
  firstName: string;
  lastName: string;
}

interface MatchResultsLogProps {
  sessionId: string;
  players: Player[];
}

interface CreateFormState {
  playerId: string;
  opponentPlayerId: string;
  opponentName: string;
}

const EMPTY_CREATE: CreateFormState = {
  playerId: '',
  opponentPlayerId: '',
  opponentName: '',
};

export function MatchResultsLog({ sessionId, players }: MatchResultsLogProps) {
  const { data: results } = useSessionMatchResults(sessionId);
  const upsert = useUpsertMatchResult();
  const updateScore = useUpdateMatchScore();
  const remove = useDeleteMatchResult();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFormState>(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const [editingScoreId, setEditingScoreId] = useState<string | null>(null);
  const [scoreInput, setScoreInput] = useState('');
  const [savingScore, setSavingScore] = useState(false);

  // Stable cancel function so it can be used in the click-outside effect
  const cancelEditScore = useCallback(() => {
    setEditingScoreId(null);
    setScoreInput('');
  }, []);

  // Close score entry when clicking outside any card marked data-score-card
  useEffect(() => {
    if (!editingScoreId) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('[data-score-card]')) {
        cancelEditScore();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [editingScoreId, cancelEditScore]);

  const openCreateForm = () => {
    cancelEditScore(); // collapse any open score entry first
    setShowCreateForm(true);
  };

  const closeCreateForm = () => {
    setCreateForm(EMPTY_CREATE);
    setShowCreateForm(false);
  };

  const resolvedOpponentName = (() => {
    if (createForm.opponentPlayerId === 'other') return createForm.opponentName.trim();
    const p = players.find((pl) => pl.id === createForm.opponentPlayerId);
    return p ? `${p.firstName} ${p.lastName}` : '';
  })();

  const canCreate = !!createForm.playerId && !!resolvedOpponentName;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      await upsert.mutateAsync({
        sessionId,
        playerId: createForm.playerId,
        opponentName: resolvedOpponentName,
        // Only set when opponent is a registered session player (not external)
        opponentPlayerId: createForm.opponentPlayerId !== 'other'
          ? createForm.opponentPlayerId
          : undefined,
      });
      setCreateForm(EMPTY_CREATE);
      setShowCreateForm(false);
    } finally {
      setCreating(false);
    }
  };

  const liveOutcome = deriveOutcome(scoreInput);
  const canSaveScore = !!scoreInput.trim() && liveOutcome !== null;

  const handleSaveScore = async (resultId: string) => {
    if (!canSaveScore) return;
    setSavingScore(true);
    try {
      await updateScore.mutateAsync({
        resultId,
        score: scoreInput.trim(),
        outcome: liveOutcome!,
      });
      cancelEditScore();
    } finally {
      setSavingScore(false);
    }
  };

  const toggleScoreEdit = (resultId: string, existingScore?: string) => {
    if (editingScoreId === resultId) {
      cancelEditScore();
    } else {
      setEditingScoreId(resultId);
      setScoreInput(existingScore ?? '');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Trophy className="w-4 h-4" />
            Match Results
            {results.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                ({results.length})
              </span>
            )}
          </CardTitle>
          {!showCreateForm && players.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={openCreateForm}
              className="gap-1.5 h-8"
            >
              <Plus className="w-3.5 h-3.5" />
              Create Matchup
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        {results.length === 0 && !showCreateForm && (
          <p className="text-sm text-muted-foreground">No matchups created yet.</p>
        )}

        {results.map((result) => {
          const isEditing = editingScoreId === result.id;
          const hasScore = !!result.score;

          return (
            <div
              key={result.id}
              className="border rounded-lg overflow-hidden"
              data-score-card
            >
              {/* Entire row is the tap target to expand score entry */}
              <div
                className={cn(
                  'flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none transition-colors',
                  isEditing ? 'bg-muted/30' : 'hover:bg-muted/20 active:bg-muted/40'
                )}
                onClick={() => toggleScoreEdit(result.id, result.score)}
              >
                {/* Outcome badge */}
                {hasScore ? (
                  <span className={cn(
                    'text-xs font-bold px-1.5 py-0.5 rounded shrink-0 w-6 text-center',
                    result.outcome === 'win'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                  )}>
                    {result.outcome === 'win' ? 'W' : 'L'}
                  </span>
                ) : (
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded shrink-0 w-6 text-center bg-muted text-muted-foreground">
                    –
                  </span>
                )}

                {/* Player name — right-aligned toward the VS centre */}
                <span className="text-sm font-medium min-w-0 flex-1 truncate text-right">
                  {result.playerName}
                </span>

                {/* Score + VS — centred fixed column */}
                <span className="text-xs text-muted-foreground shrink-0 text-center px-1 flex flex-col items-center leading-tight">
                  {hasScore && (
                    <span className="font-mono">{result.score}</span>
                  )}
                  <span className="font-semibold">vs</span>
                </span>

                {/* Opponent — left-aligned away from the VS centre */}
                <span className="text-sm min-w-0 flex-1 truncate text-muted-foreground">
                  {result.opponentName}
                </span>

                {/* Trash — stops propagation so it doesn't toggle the score editor */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    remove.mutateAsync(result.id);
                  }}
                  className="text-muted-foreground hover:text-destructive transition-colors p-1 shrink-0"
                  aria-label="Delete matchup"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Inline score entry — expands below the row */}
              {isEditing && (
                <div className="border-t px-3 pb-3 pt-2 space-y-2 bg-muted/20">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      Score{' '}
                      <span className="text-muted-foreground font-normal">
                        — {result.playerName}'s games first
                      </span>
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="e.g. 6-3, 7-5"
                        value={scoreInput}
                        onChange={(e) => setScoreInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && canSaveScore) handleSaveScore(result.id);
                          if (e.key === 'Escape') cancelEditScore();
                        }}
                        className="text-sm"
                        autoFocus
                      />
                      {scoreInput.trim() && (
                        <span className={cn(
                          'text-xs font-bold px-2 py-1 rounded shrink-0 min-w-[2rem] text-center',
                          liveOutcome === 'win'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                            : liveOutcome === 'loss'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                            : 'bg-muted text-muted-foreground'
                        )}>
                          {liveOutcome === 'win' ? 'W' : liveOutcome === 'loss' ? 'L' : '?'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSaveScore(result.id)}
                      disabled={savingScore || !canSaveScore}
                      className="flex-1"
                    >
                      {savingScore ? 'Saving…' : 'Save Score'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEditScore}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Create Matchup form */}
        {showCreateForm && (
          <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">New Matchup</span>
              <button onClick={closeCreateForm} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Player</Label>
              <select
                value={createForm.playerId}
                onChange={(e) => setCreateForm((f) => ({ ...f, playerId: e.target.value }))}
                className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select player…</option>
                {players.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.firstName} {p.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Opponent</Label>
              <select
                value={createForm.opponentPlayerId}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, opponentPlayerId: e.target.value, opponentName: '' }))
                }
                className="w-full text-sm border border-input rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select opponent…</option>
                {players
                  .filter((p) => p.id !== createForm.playerId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </option>
                  ))}
                <option value="other">Other (external)…</option>
              </select>
              {createForm.opponentPlayerId === 'other' && (
                <Input
                  placeholder="Opponent name"
                  value={createForm.opponentName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, opponentName: e.target.value }))}
                  className="text-sm mt-1"
                />
              )}
            </div>

            <Button
              onClick={handleCreate}
              disabled={creating || !canCreate}
              className="w-full"
              size="sm"
            >
              {creating ? 'Creating…' : 'Create Matchup'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
